import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Search, Wrench, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import Mascot from "@/components/Mascot";

export default function ServiceOrders() {
  const { user, hasPerm } = useAuth();
  const canDelete = hasPerm("service_orders.delete");
  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filter, setFilter] = useState("");
  const [month, setMonth] = useState("");
  const [q, setQ] = useState("");
  const [confirmOS, setConfirmOS] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    if (month) params.set("month", month);
    if (q) params.set("q", q);
    const { data } = await api.get(`/service-orders?${params.toString()}`);
    setOrders(data);
  };

  const doDelete = async () => {
    const o = confirmOS;
    setConfirmOS(null);
    try {
      await api.delete(`/service-orders/${o.id}`);
      toast.success(`OS ${o.number} excluída`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Não foi possível excluir"); }
  };

  useEffect(() => { api.get("/settings/workflow-status").then((r) => setStatuses(r.data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [filter, month, q, user?.active_store_id]);

  const statusMap = Object.fromEntries(statuses.map((s) => [s.key, s]));

  return (
    <div className="space-y-6" data-testid="service-orders-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Ordens de Serviço</h1>
          <p className="text-zinc-400 mt-1">{orders.length} ordens nesta loja.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="os-month"
            className="bg-[#121217] border border-white/10 rounded-lg px-3 text-sm text-white h-10" />
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="os-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="OS, cliente, modelo, código..." className="pl-9 bg-[#121217] border-white/10 text-white" />
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Chip active={filter === ""} onClick={() => setFilter("")} label="Todas" />
        {statuses.map((s) => <Chip key={s.key} active={filter === s.key} onClick={() => setFilter(s.key)} label={s.label} color={s.color} />)}
      </div>

      {orders.length === 0 ? (
        <div className="alpha-card p-12 text-center">
          <Mascot size={90} className="mx-auto mb-4" />
          <p className="text-zinc-400">Nenhuma ordem encontrada. Use o botão Alpha para abrir a primeira.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {orders.map((o) => {
            const st = statusMap[o.status] || {};
            return (
              <div key={o.id} data-testid={`os-row-${o.number}`} className="rounded-xl border border-white/10 bg-[#0E0E12] p-4 hover:border-gold/30 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-gold/10 flex items-center justify-center shrink-0"><Wrench size={18} className="text-gold" /></div>
                  <button onClick={() => navigate(`/app/os/${o.id}`)} data-testid={`os-open-${o.number}`} className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-kpi text-gold-bright font-bold">{o.number}</span>
                      <span className="text-white font-medium truncate">{o.customerName}</span>
                      <span className="text-[10px] font-mono-kpi text-zinc-500">{o.withdrawalCode}</span>
                    </div>
                    <div className="text-sm text-zinc-500 truncate mt-0.5">{o.device?.brand} {o.device?.model} · {o.reportedIssue}</div>
                  </button>
                  <span className="text-xs px-2.5 py-0.5 rounded-full font-medium shrink-0 whitespace-nowrap" style={{ background: `${st.color || "#666"}22`, color: st.color || "#aaa" }}>{st.label || o.status}</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 sm:pl-14">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono-kpi text-white font-semibold">{money(o.chargedAmount)}</span>
                    <span className="text-[11px] text-zinc-600">{new Date(o.createdAt).toLocaleDateString("pt-BR")}</span>
                    {o.invoiced && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">Faturada</span>}
                  </div>
                  {canDelete && !o.invoiced && (
                    <button data-testid={`os-delete-${o.number}`} onClick={() => setConfirmOS(o)} className="text-[11px] px-2.5 py-1 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center gap-1 shrink-0"><Trash2 size={12} />Excluir</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmOS} onOpenChange={(v) => !v && setConfirmOS(null)}>
        <AlertDialogContent className="bg-[#121217] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir ordem de serviço?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              A OS <b className="text-zinc-200">{confirmOS?.number}</b> de {confirmOS?.customerName} será removida da lista. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="os-delete-cancel" className="bg-transparent border-white/15 text-zinc-300 hover:bg-white/5">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="os-delete-confirm" onClick={doDelete} className="bg-red-500/90 hover:bg-red-500 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Chip({ active, onClick, label, color }) {
  return (
    <button onClick={onClick} data-testid={`os-filter-${label}`}
      className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? "bg-gold/15 border-gold/40 text-gold-bright" : "border-white/10 text-zinc-400 hover:text-white"}`}>
      {color && <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: color }} />}
      {label}
    </button>
  );
}
